import { __rest, __assign } from '../node_modules/tslib/tslib.es6.esm.js';
import * as React from 'react';
import InfoCircleStroke from '../assets/icons/InfoCircleStroke.esm.js';
import classNames from 'classnames';

var NvSwitch = React.forwardRef(function NvSwitch(_a, ref) {
    var checked = _a.checked, defaultChecked = _a.defaultChecked, onKeyDownCapture = _a.onKeyDownCapture, onKeyPressCapture = _a.onKeyPressCapture, onKeyUpCapture = _a.onKeyUpCapture, disabled = _a.disabled, label = _a.label, description = _a.description, helperIcon = _a.helperIcon, helperIconClickHandler = _a.helperIconClickHandler, id = _a.id, props = __rest(_a, ["checked", "defaultChecked", "onKeyDownCapture", "onKeyPressCapture", "onKeyUpCapture", "disabled", "label", "description", "helperIcon", "helperIconClickHandler", "id"]);
    /**
     * The "disabled" attribute changes the way a DOM element look and behaves. It makes the component appear faded and grayed out and
     * it also disables any kind of event the element would receive (like mouse, touch and keyboard events), but it also causes
     * accessibility problems by making the element invisible to screen readers.
     *
     * To avoid this problem the use of "aria-disabled" attribute is recommended, but the only thing "aria-disabled" does is inform screen readers
     * that that element is disabled. It does not stop events nor changes the element appearance. So in order to provide the best experience
     * we have to provide "aria-disabled" the features it misses.
     *
     * The faded/grayed out appearance is being added by the following css
     * *******
     * &[aria-disabled='true'] + .switch {
     *   background: $gray6;
     *   border-color: $gray3;
     *   cursor: not-allowed;
     *
     *   .pointer {
     *     background: $gray3;
     *
     *     &::after {
     *       background: $gray3;
     *     }
     *   }
     * }
     * *******
     *
     * To stop mouse and touch events the following css is being applied to NvSwitch when "aria-disabled" is used.
     * *******
     *  .input[aria-disabled='true'] {
     *    pointer-events: none;
     *  }
     * *******
     *
     * And finally to stop keyboard events the "conditionallyDisableKeyboardEvents" function is being used, it stands in the middle of the capture
     * phase of all keyboard events that get to this element and stop their propagation causing the element not to receive keyboard events in case
     * it is disabled.
     */
    var conditionallyDisableKeyboardEvents = React.useCallback(function (e) {
        if (disabled) {
            e.nativeEvent.stopPropagation();
            e.nativeEvent.preventDefault();
            return;
        }
        switch (e.nativeEvent.type) {
            case 'keydown':
                return onKeyDownCapture === null || onKeyDownCapture === void 0 ? void 0 : onKeyDownCapture(e);
            case 'keypress':
                return onKeyPressCapture === null || onKeyPressCapture === void 0 ? void 0 : onKeyPressCapture(e);
            case 'keyup':
                return onKeyUpCapture === null || onKeyUpCapture === void 0 ? void 0 : onKeyUpCapture(e);
        }
    }, [disabled, onKeyDownCapture, onKeyPressCapture, onKeyUpCapture]);
    return (React.createElement("div", { className: "nv-switch" },
        React.createElement("label", { "aria-disabled": Boolean(disabled), className: "nv-switch-label", htmlFor: id },
            React.createElement("input", __assign({}, props, { "aria-describedby": "description-".concat(id), "aria-disabled": Boolean(disabled), "aria-labelledby": "label-".concat(id), checked: checked, className: "nv-switch-input", defaultChecked: defaultChecked, id: id, onKeyDownCapture: conditionallyDisableKeyboardEvents, onKeyPressCapture: conditionallyDisableKeyboardEvents, onKeyUpCapture: conditionallyDisableKeyboardEvents, ref: ref, role: "switch", type: "checkbox" })),
            React.createElement("div", { className: "nv-switch-switcher" },
                React.createElement("span", { className: "nv-switch-pointer" })),
            React.createElement("div", { className: "nv-switch-text-wrapper" },
                React.createElement("span", { id: "label-".concat(id) },
                    label,
                    helperIcon && (React.createElement("span", { className: "nv-switch-helper-icon", onClick: helperIconClickHandler, onKeyDown: helperIconClickHandler, role: "button", tabIndex: 0 },
                        React.createElement(InfoCircleStroke, null)))),
                React.createElement("p", { className: classNames('nv-fine-print-1', 'nv-switch-description'), id: "description-".concat(id) }, description)))));
});

export { NvSwitch as default };
//# sourceMappingURL=NvSwitch.esm.js.map
